import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useAudioCapture, AudioChunk } from '../hooks/useAudioCapture';
import { useStreamerWebSocket } from '../hooks/useWebSocket';
import { AudioError, ConnectionError, StatusIndicator } from './ErrorDisplay';
import { AudioErrorBoundary, WebSocketErrorBoundary } from './ErrorBoundary';
import { errorHandler, performanceMonitor } from '../lib/errorHandler';
import { 
  Mic, 
  MicOff, 
  Play, 
  Square, 
  Wifi, 
  WifiOff, 
  Copy, 
  Share2,
  AlertCircle,
  Volume2,
  Users
} from 'lucide-react';
import { cn } from '../lib/utils';

interface StreamerInterfaceProps {
  className?: string;
}

export const StreamerInterface: React.FC<StreamerInterfaceProps> = ({ className }) => {
  const [roomId, setRoomId] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [viewerCount] = useState(0);
  const [copiedRoomId, setCopiedRoomId] = useState(false);

  // Generate room ID on component mount
  useEffect(() => {
    const generateRoomId = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let result = '';
      for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };

    if (!roomId) {
      setRoomId(generateRoomId());
    }
  }, [roomId]);

  // WebSocket connection for streaming
  const webSocket = useStreamerWebSocket(roomId, {
    autoConnect: false,
    maxReconnectAttempts: 5,
    initialReconnectDelay: 2000
  });

  // Audio capture hook
  const audioCapture = useAudioCapture({
    chunkInterval: 1500,
    onAudioChunk: handleAudioChunk,
    onError: (error) => {
      console.error('Audio capture error:', error);
      // Only show error to user if it's not a format support issue during initialization
      // Format support issues are handled by the UI showing disabled state
      if (error.type !== 'FORMAT_UNSUPPORTED') {
        errorHandler.handleAudioError(new Error(error.message), {
          component: 'StreamerInterface',
          operation: 'audio_capture',
          metadata: { errorType: error.type }
        });
      }
    }
  });

  // Handle audio chunks and send via WebSocket
  function handleAudioChunk(chunk: AudioChunk) {
    const endTimer = performanceMonitor.startTimer('audio_chunk_processing');
    
    if (webSocket.isConnected && isStreaming) {
      // Convert Blob to ArrayBuffer
      chunk.data.arrayBuffer().then(arrayBuffer => {
        const success = webSocket.sendAudioChunk(arrayBuffer, chunk.timestamp, chunk.sequenceId);
        if (!success) {
          errorHandler.handleWebSocketError(
            new Error('Failed to send audio chunk'),
            {
              component: 'StreamerInterface',
              operation: 'send_audio_chunk',
              metadata: { chunkSize: arrayBuffer.byteLength, sequenceId: chunk.sequenceId }
            }
          );
        }
        endTimer();
      }).catch(error => {
        endTimer();
        errorHandler.handleAudioError(error, {
          component: 'StreamerInterface',
          operation: 'audio_chunk_conversion'
        });
      });
    } else {
      endTimer();
    }

    // Simulate audio level for visualization (in real app, you'd analyze the audio data)
    const simulatedLevel = Math.random() * 100;
    setAudioLevel(simulatedLevel);
  }

  // Handle WebSocket messages
  useEffect(() => {
    if (webSocket.lastMessage) {
      const message = webSocket.lastMessage;
      
      if (message.type === 'status') {
        // Handle status updates, viewer count, etc.
        console.log('Status update:', message);
      }
    }
  }, [webSocket.lastMessage]);

  // Start streaming
  const handleStartStream = useCallback(async () => {
    const endTimer = performanceMonitor.startTimer('start_streaming');
    
    try {
      // Connect to WebSocket first
      if (!webSocket.isConnected) {
        await webSocket.connect();
      }

      // Start the stream on the server
      const streamStarted = webSocket.startStream();
      if (!streamStarted) {
        throw new Error('Failed to start stream on server');
      }

      // Start audio capture
      await audioCapture.startRecording();
      
      setIsStreaming(true);
      endTimer();
    } catch (error) {
      endTimer();
      errorHandler.handleError(
        error instanceof Error ? error : new Error('Failed to start stream'),
        'COMPONENT_ERROR',
        {
          component: 'StreamerInterface',
          operation: 'start_streaming',
          roomId
        }
      );
    }
  }, [webSocket, audioCapture, roomId]);

  // Stop streaming
  const handleStopStream = useCallback(() => {
    audioCapture.stopRecording();
    setIsStreaming(false);
    setAudioLevel(0);
  }, [audioCapture]);

  // Copy room ID to clipboard
  const handleCopyRoomId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(roomId);
      setCopiedRoomId(true);
      setTimeout(() => setCopiedRoomId(false), 2000);
    } catch (error) {
      console.error('Failed to copy room ID:', error);
    }
  }, [roomId]);

  // Share room ID
  const handleShareRoomId = useCallback(async () => {
    const shareUrl = `${window.location.origin}/viewer?room=${roomId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my live audio stream',
          text: `Join my live audio transcription stream with room ID: ${roomId}`,
          url: shareUrl
        });
      } catch (error) {
        console.error('Failed to share:', error);
        // Fallback to copying URL
        await navigator.clipboard.writeText(shareUrl);
      }
    } else {
      // Fallback to copying URL
      await navigator.clipboard.writeText(shareUrl);
    }
  }, [roomId]);

  // Connection status indicator
  const getConnectionStatus = () => {
    if (webSocket.status === 'connected') {
      return { icon: Wifi, color: 'text-green-500', text: 'Connected' };
    } else if (webSocket.status === 'connecting') {
      return { icon: Wifi, color: 'text-yellow-500', text: 'Connecting...' };
    } else if (webSocket.status === 'reconnecting') {
      return { icon: Wifi, color: 'text-orange-500', text: 'Reconnecting...' };
    } else if (webSocket.status === 'error') {
      return { icon: WifiOff, color: 'text-red-500', text: 'Connection Error' };
    } else {
      return { icon: WifiOff, color: 'text-gray-500', text: 'Disconnected' };
    }
  };

  const connectionStatus = getConnectionStatus();
  const ConnectionIcon = connectionStatus.icon;

  return (
    <WebSocketErrorBoundary>
      <AudioErrorBoundary>
        <div className={cn("max-w-2xl mx-auto p-6 space-y-6", className)}>
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold">Audio Streamer</h1>
            <p className="text-muted-foreground">
              Stream your audio with real-time transcription for viewers
            </p>
          </div>

          {/* Connection Error Banner */}
          {webSocket.status === 'error' && webSocket.error && (
            <ConnectionError
              isConnecting={false}
              onRetry={() => webSocket.connect()}
            />
          )}

      {/* Room Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Room Information
          </CardTitle>
          <CardDescription>
            Share this room ID with viewers to let them join your stream
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className="text-sm font-medium">Room ID</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 px-3 py-2 bg-muted rounded-md font-mono text-lg tracking-wider">
                  {roomId}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyRoomId}
                  className="shrink-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {copiedRoomId && (
            <div className="text-sm text-green-600 font-medium">
              ✓ Room ID copied to clipboard!
            </div>
          )}

          <Button 
            onClick={handleShareRoomId}
            variant="outline" 
            className="w-full"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share Room
          </Button>
        </CardContent>
      </Card>

      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ConnectionIcon className={cn("h-5 w-5", connectionStatus.color)} />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Status:</span>
            <StatusIndicator 
              status={webSocket.status}
              error={webSocket.error?.message || undefined}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Viewers:</span>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono">{viewerCount}</span>
            </div>
          </div>

          {webSocket.error && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-destructive">Connection Error</div>
                <div className="text-destructive/80">{webSocket.error.message}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audio Controls Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isStreaming ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            Audio Controls
          </CardTitle>
          <CardDescription>
            {audioCapture.isSupported 
              ? "Control your audio stream and monitor levels"
              : "WebM/Opus format is not supported in this browser"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Audio Format Support */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Audio Format:</span>
            <Badge variant={audioCapture.isSupported ? 'default' : 'destructive'}>
              {audioCapture.isSupported ? 'WebM/Opus Supported' : 'Not Supported'}
            </Badge>
          </div>

          {/* Audio Level Visualization */}
          {isStreaming && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Audio Level:</span>
                <div className="flex items-center gap-1">
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-sm">{Math.round(audioLevel)}%</span>
                </div>
              </div>
              <Progress value={audioLevel} className="h-2" />
            </div>
          )}

          {/* Stream Controls */}
          <div className="flex gap-3">
            {!isStreaming ? (
              <Button
                onClick={handleStartStream}
                disabled={!audioCapture.isSupported || webSocket.status === 'connecting' || webSocket.status === 'reconnecting'}
                className="flex-1"
                size="lg"
              >
                <Play className="h-4 w-4 mr-2" />
                Start Streaming
              </Button>
            ) : (
              <Button
                onClick={handleStopStream}
                variant="destructive"
                className="flex-1"
                size="lg"
              >
                <Square className="h-4 w-4 mr-2" />
                Stop Streaming
              </Button>
            )}
          </div>

          {/* Recording Indicator */}
          {isStreaming && (
            <div className="flex items-center justify-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-red-700 font-medium">Live Streaming</span>
            </div>
          )}

          {/* Audio Error Display */}
          {audioCapture.error && (
            <AudioError
              errorType={
                audioCapture.error.type === 'PERMISSION_DENIED' ? 'permission' :
                audioCapture.error.type === 'FORMAT_UNSUPPORTED' ? 'format' :
                'device'
              }
              onRetry={() => audioCapture.startRecording()}
            />
          )}
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle>How to Stream</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              1
            </div>
            <div>Share your Room ID with viewers so they can join your stream</div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              2
            </div>
            <div>Click "Start Streaming" and allow microphone access when prompted</div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              3
            </div>
            <div>Speak normally - your audio will be transcribed in real-time for viewers</div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              4
            </div>
            <div>Monitor your connection status and audio levels during streaming</div>
          </div>
        </CardContent>
      </Card>
        </div>
      </AudioErrorBoundary>
    </WebSocketErrorBoundary>
  );
};